import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, LucideX, LucideEllipsis } from "lucide-react";
import TagsOptions from "@/components/TagsPicker/TagsOptions";
import "@/assets/css/TagsPicker.scss";
import { Skeleton } from "@mui/material";
import { setSelectedCompanyTags } from "@/dispatchers/patientDispatchers";

export default function TagsPicker({
  isDarkMode,
  isCompanyTagsLoading,
  tagsOptions,
  setTagsOptions,
  toggleTagsPickerDropdown,
  isOpenTagsPickerDropdown,
  setIsOpenTagsPickerDropdown,
  selectedCompanyTags,
  tagFilterValue,
  setTagFilterValue,
  generateRandomTagsColor,
  previewTagColor,
  setPreviewTagColor,
  handleSelectTag,
  handleAddCompanyTag,
  currentTag,
  setCurrentTag,
  handleDeleteTag,
  confirmDelete,
  editingTag,
  setEditingTag,
}) {
  const [openUpwards, setOpenUpwards] = useState(false);

  const dropdownRef = useRef(null);

  const safeTags = Array.isArray(selectedCompanyTags)
    ? selectedCompanyTags
    : [];

  useEffect(() => {
    if (tagFilterValue.trim()) setPreviewTagColor(generateRandomTagsColor());
    else setPreviewTagColor("");
  }, [tagFilterValue]);

  useEffect(() => {
    if (isOpenTagsPickerDropdown && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 200;
      setOpenUpwards(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
    }
  }, [isOpenTagsPickerDropdown]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpenTagsPickerDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [tagToRemove, setTagToRemove] = useState(null);

  useEffect(() => {
    if (!tagToRemove) return;

    const newTags = Array.isArray(selectedCompanyTags)
      ? selectedCompanyTags.filter((t) => t.tagName !== tagToRemove.tagName)
      : [];

    setSelectedCompanyTags(newTags);

    setTagToRemove(null);
  }, [tagToRemove]);

  return (
    <div className="relative w-full mt-1" ref={dropdownRef}>
      <div
        className="flex items-center justify-between h-[38px] px-2 py-1 border border-gray-300 rounded-md cursor-pointer dark:border-gray-600 dark:bg-gray-800"
        onClick={toggleTagsPickerDropdown}
      >
        <div className="flex flex-wrap gap-1">
          {safeTags.length === 0 ? (
            <span className="text-sm text-gray-400 dark:text-gray-500">
              Select tags
            </span>
          ) : (
            <div className="flex gap-2 align-center">
              {isCompanyTagsLoading
                ? [...Array(2)].map((_, i) => (
                    <Skeleton
                      animation="wave"
                      variant="text"
                      width={50}
                      height={22}
                      className="mb-2"
                      sx={{ bgcolor: isDarkMode ? "grey.700" : "" }}
                    />
                  ))
                : safeTags.slice(0, 2).map((tag) => (
                    <span
                      key={tag.tagName}
                      className="text-white text-[12px] px-2 py-[2px] rounded font-semibold"
                      style={{ backgroundColor: tag.colour }}
                    >
                      {tag.tagName}
                    </span>
                  ))}
              {safeTags.length > 2 && (
                <div className="p-1 flex items-center text-[11px] rounded bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                  <span>+ {safeTags.length - 2}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${
            isOpenTagsPickerDropdown ? "rotate-180" : ""
          }`}
        />
      </div>

      {isOpenTagsPickerDropdown && (
        <div
          className={`absolute z-10 pb-3 w-full bg-white border border-gray-300 rounded-md shadow-lg dark:bg-gray-700 dark:border-gray-600 ${
            openUpwards ? "bottom-full mb-1" : "top-full mt-1"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {safeTags.length > 0 && (
            <div className="flex flex-wrap gap-2 p-1">
              {safeTags.map((tag) => (
                <span
                  key={tag.tagName}
                  className="relative inline-flex items-center text-white text-[12px] px-2 py-[2px] rounded font-semibold hovered-tag group"
                  style={{ backgroundColor: tag.colour }}
                >
                  {tag.tagName}
                  <button
                    className="absolute right-0 flex items-center h-full px-2 text-white transition-opacity duration-150 opacity-0 group-hover:opacity-100 hover:text-gray-200"
                    style={{ backgroundColor: tag.colour }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setTagToRemove(tag);
                    }}
                  >
                    <LucideX size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="p-1 border-b border-grey-500">
            <input
              type="text"
              value={tagFilterValue}
              onChange={(e) => setTagFilterValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddCompanyTag();
                }
              }}
              placeholder="Search or add tags..."
              className="w-full p-1 text-sm border-none rounded outline-none focus:outline-none dark:bg-gray-700 dark:text-white"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {tagFilterValue &&
            !(
              Array.isArray(tagsOptions) &&
              tagsOptions.some(
                (t) => t.tagName.toLowerCase() === tagFilterValue.toLowerCase()
              )
            ) && (
              <div
                className="flex items-center gap-2 p-1 text-sm rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => handleAddCompanyTag(tagFilterValue, false)}
              >
                <span className="text-gray-500">Create</span>
                <span
                  className="text-white text-[12px] px-2 py-[2px] rounded font-semibold"
                  style={{ backgroundColor: previewTagColor }}
                >
                  {tagFilterValue}
                </span>
              </div>
            )}

          <div
            className="overflow-y-auto tags-list max-h-[200px]"
            style={{ overscrollBehavior: "contain" }}
            onWheel={(e) => e.stopPropagation()}
          >
            {isCompanyTagsLoading ? (
              <div className="p-2">
                {[...Array(3)].map((_, i) => (
                  <Skeleton
                    key={i}
                    animation="wave"
                    variant="text"
                    width={100}
                    height={32}
                    className="mb-2"
                    sx={{ bgcolor: isDarkMode ? "grey.700" : "" }}
                  />
                ))}
              </div>
            ) : (
              Array.isArray(tagsOptions) &&
              tagsOptions
                .filter((tag) =>
                  tag?.tagName
                    ?.toLowerCase()
                    .includes(tagFilterValue.toLowerCase())
                )
                .map((tag) => (
                  <div
                    key={tag.tagName}
                    className={`h-[40px] relative group flex items-center justify-between w-full p-2 cursor-pointer rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      safeTags.some((t) => t.tagName === tag.tagName)
                        ? "bg-gray-100 dark:bg-gray-700"
                        : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectTag(tag);
                    }}
                  >
                    <span
                      className="text-white text-[12px] px-2 py-[2px] rounded font-semibold"
                      style={{ backgroundColor: tag.colour }}
                    >
                      {tag.tagName}
                    </span>
                    <TagsOptions
                      currentTag={tag.tagName}
                      setCurrentTag={setCurrentTag}
                      tagsOptions={tagsOptions}
                      setTagsOptions={setTagsOptions}
                      handleAddCompanyTag={handleAddCompanyTag}
                      handleDeleteTag={handleDeleteTag}
                      confirmDelete={confirmDelete}
                      tagId={tag.tagId}
                      editingTag={editingTag}
                      setEditingTag={setEditingTag}
                    >
                      <LucideEllipsis className="hidden w-4 h-4 text-gray-400 cursor-pointer lucide-ellipsis dark:text-gray-300 group-hover:inline" />
                    </TagsOptions>
                  </div>
                ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
